// Service worker e notifiche push dell'app installata (PWA).
//
// Come funziona, in breve:
// - il service worker (public/sw.js) si registra al primo caricamento: serve anche per la cache;
// - il PERMESSO delle notifiche NON si chiede mai da solo: lo chiede la card «Attiva le notifiche» della home,
//   dopo il login, quando il cliente tocca il bottone (i browser danno il permesso solo dopo un tocco, e lo
//   chiedono una volta sola: se si nega, si riapre solo dalle impostazioni del telefono);
// - su iPhone le push arrivano SOLO se l'app è stata aggiunta alla schermata Home (iOS 16.4 e successivi):
//   fuori dall'app installata pushManager.subscribe fallisce, e la card lo spiega;
// - ogni dispositivo ha la SUA iscrizione, salvata sul server con nome e data (Profilo → Notifiche);
// - se il browser cambia l'iscrizione da solo, il service worker lascia il rinnovo in una cache e qui lo si manda
//   al server alla prima apertura dell'app (pushsubscriptionchange).
import { API } from './api';
import { leggiToken } from './sessione';
import { piattaforma } from './passkey';

const CHIAVE_DISPOSITIVO = 'mk_push_dispositivo'; // { id, nome }: l'iscrizione di QUESTO dispositivo
const CACHE_RINNOVO = 'mk-push-rinnovo';          // la scrive public/sw.js
const CHIAVE_RINNOVO = '/rinnovo-push.json';

export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      ascoltaIlServiceWorker();
      return registration;
    } catch (error) {
      console.error('[Push] Errore registrazione SW:', error);
      return null;
    }
  }
  return null;
};

// Il nome con cui il dispositivo si presenta nell'elenco del profilo: "iPhone · Safari", "Android · Chrome"
export const nomeDispositivo = () => {
  try {
    return piattaforma().dispositivo;
  } catch {
    return 'Dispositivo';
  }
};

// L'app è aperta dalla schermata Home (o installata su Android)? Su iPhone è la condizione per ricevere le push.
export const appInstallata = () => {
  try {
    return Boolean(window.navigator?.standalone) || Boolean(window.matchMedia?.('(display-mode: standalone)')?.matches);
  } catch {
    return false;
  }
};

export const suIPhone = () => /iPhone|iPad|iPod/.test(navigator.userAgent || '')
  || (/Mac/.test(navigator.platform || '') && navigator.maxTouchPoints > 1);

export const notifichePossibili = () => 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

// Quello che serve alla card e alla sezione del profilo per dire la cosa giusta:
// stato: 'non_supportate' | 'da_installare' (iPhone fuori dall'app) | 'da_attivare' | 'negato' | 'attive'
export const statoNotifiche = () => {
  if (!notifichePossibili()) {
    return { stato: suIPhone() && !appInstallata() ? 'da_installare' : 'non_supportate', iphone: suIPhone(), installata: appInstallata() };
  }
  const permesso = Notification.permission;
  const installata = appInstallata();
  // iPhone e iPad: fuori dall'app aggiunta alla schermata Home le push non arrivano, nemmeno con il permesso dato
  if (suIPhone() && !installata) return { stato: 'da_installare', permesso, iphone: true, installata };
  const stato = permesso === 'granted' ? 'attive' : permesso === 'denied' ? 'negato' : 'da_attivare';
  return { stato, permesso, iphone: suIPhone(), installata };
};

// Il numero sull'icona dell'app installata (Android e iPhone dalla 16.4). Dove non c'è, non succede niente.
export const mostraBadge = async (quanti) => {
  try {
    if (typeof navigator?.setAppBadge !== 'function') return;
    if (quanti > 0) await navigator.setAppBadge(quanti);
    else await navigator.clearAppBadge();
  } catch { /* il sistema può rifiutarlo (app non installata) */ }
};

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return false;

  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

// ---------------------------------------------------------------- questo dispositivo
const leggiDispositivo = () => {
  try { return JSON.parse(localStorage.getItem(CHIAVE_DISPOSITIVO) || 'null'); } catch { return null; }
};
const segnaDispositivo = (dati) => {
  try { localStorage.setItem(CHIAVE_DISPOSITIVO, JSON.stringify(dati)); } catch { /* archivio non disponibile */ }
};
const dimenticaDispositivo = () => {
  try { localStorage.removeItem(CHIAVE_DISPOSITIVO); } catch { /* archivio non disponibile */ }
};
// L'id dell'iscrizione di questo dispositivo: il profilo lo usa per scrivere «questo» accanto alla riga giusta
export const dispositivoDiQuestoBrowser = () => leggiDispositivo();

// Con quale chiave VAPID ci siamo iscritti su QUESTO telefono. Su iPhone (Safari)
// `subscription.options.applicationServerKey` torna vuota: senza questa copia in locale non c'è modo di
// accorgersi che la chiave del server è cambiata, e il telefono resta muto per sempre (Apple risponde 403
// VapidPkHashMismatch a ogni invio). Era esattamente il bug delle notifiche che non arrivavano.
const CHIAVE_VAPID_USATA = 'mk_push_chiave';
const leggiChiaveUsata = () => {
  try { return localStorage.getItem(CHIAVE_VAPID_USATA) || null; } catch { return null; }
};
const segnaChiaveUsata = (chiave) => {
  try { localStorage.setItem(CHIAVE_VAPID_USATA, chiave || ''); } catch { /* archivio non disponibile */ }
};

// ---------------------------------------------------------------- iscrizione
const chiavePubblica = async (token) => {
  // `no-store`: la chiave del server può cambiare, e una copia vecchia in cache fa iscrivere il telefono con la
  // chiave sbagliata — il servizio push poi rifiuta tutto e la notifica «risulta inviata» senza arrivare
  const response = await fetch(`${API}/push/vapid-key`, {
    cache: 'no-store',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return null;
  const { publicKey } = await response.json();
  return publicKey || null;
};

const salvaSulServer = async (token, corpo) => {
  const risposta = await fetch(`${API}/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(corpo),
  });
  if (!risposta.ok) return null;
  return risposta.json().catch(() => null);
};

// Una sola iscrizione per volta, qualunque strada la chieda. Dopo il login partono insieme allineaNotifiche()
// (AuthContext) e il banner dello staff: tutte e due trovavano getSubscription() vuoto e ne chiedevano una nuova,
// così sul server nascevano DUE dispositivi per lo stesso computer (otto righe «Mac · Chrome» dopo tre accessi).
// Adesso la prima chiamata tiene la promessa qui e chi arriva dopo aspetta quella: una richiesta sola, un dispositivo solo.
let iscrizioneInCorso = null;

// `forza`: «Riattiva le notifiche». Non basta ri-iscriversi, perché l'iscrizione che c'è sembra buona al
// browser: va prima **cancellata** (unsubscribe) e poi rifatta, se no il telefono resta con quella che il
// servizio push rifiuta.
export const subscribeToPush = (retries = 3, forza = false) => {
  if (iscrizioneInCorso && !forza) return iscrizioneInCorso;
  iscrizioneInCorso = iscrivi(retries, forza);
  const libera = () => { iscrizioneInCorso = null; };
  iscrizioneInCorso.then(libera, libera);
  return iscrizioneInCorso;
};

export const riattivaPush = () => subscribeToPush(3, true);

const iscrivi = async (retries = 3, forza = false) => {
  try {
    if (!('serviceWorker' in navigator)) return null;

    // Aspetta che il SW sia ready (con timeout)
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => setTimeout(() => reject(new Error('SW ready timeout')), 10000)),
    ]);

    // Aspetta che il SW sia attivo
    if (registration.installing || registration.waiting) {
      await new Promise((resolve) => {
        const sw = registration.installing || registration.waiting;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'activated') resolve();
        });
        setTimeout(resolve, 5000); // Timeout 5s
      });
    }

    if (!registration.pushManager) return null;

    const token = leggiToken();
    const publicKey = await chiavePubblica(token);
    if (!publicKey) return null;

    // L'iscrizione di prima: se è ancora buona la si tiene (così il dispositivo resta lo stesso sul server),
    // altrimenti la si rifà. Il suo indirizzo viaggia come `endpoint_precedente`: il server aggiorna QUELLA riga.
    let subscription = await registration.pushManager.getSubscription();
    let endpointPrecedente = subscription ? subscription.endpoint : null;
    if (subscription && forza) {
      await subscription.unsubscribe();   // «Riattiva»: si butta quella che c'è, qualunque sia
      subscription = null;
    }
    if (subscription && !stessaChiave(subscription, publicKey)) {
      await subscription.unsubscribe();   // la chiave del server è cambiata: l'iscrizione vecchia non vale più
      subscription = null;
    }

    for (let i = 0; !subscription && i < retries; i += 1) {
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      } catch (subError) {
        // su iPhone fuori dall'app installata finisce sempre qui: è il sistema che non lo permette
        if (i < retries - 1) await new Promise((r) => setTimeout(r, 2000));
      }
    }

    if (!subscription) return null;

    if (token) {
      const dispositivo = await salvaSulServer(token, {
        subscription: subscription.toJSON(),
        nome_dispositivo: nomeDispositivo(),
        endpoint_precedente: endpointPrecedente !== subscription.endpoint ? endpointPrecedente : null,
        chiave_pubblica: publicKey,   // il server riconosce le iscrizioni fatte con una chiave di prima
      });
      segnaChiaveUsata(publicKey);
      if (dispositivo?.id) segnaDispositivo({ id: dispositivo.id, nome: dispositivo.nome_dispositivo });
    }

    return subscription;
  } catch (error) {
    console.error('[Push] Errore:', error.message);
    return null;
  }
};

// L'iscrizione è stata fatta con la chiave che il server usa adesso? (le chiavi VAPID si possono cambiare)
const stessaChiave = (subscription, publicKey) => {
  try {
    const chiave = subscription.options?.applicationServerKey;
    if (!chiave) {
      // Safari e iOS non la dicono: si confronta con la copia che abbiamo tenuto noi al momento dell'iscrizione.
      // Se non ce l'abbiamo (iscrizione vecchia, di prima di questa correzione) è più sicuro rifarla: una
      // iscrizione buona rifatta non costa niente, una rotta non si accorge nessuno.
      const usata = leggiChiaveUsata();
      return usata ? usata === publicKey : false;
    }
    const byte = new Uint8Array(chiave);
    const atteso = urlBase64ToUint8Array(publicKey);
    return byte.length === atteso.length && byte.every((v, i) => v === atteso[i]);
  } catch {
    return true;
  }
};

// ---------------------------------------------------------------- rinnovo lasciato dal service worker
const leggiRinnovo = async () => {
  try {
    if (!('caches' in window)) return null;
    const cache = await caches.open(CACHE_RINNOVO);
    const risposta = await cache.match(CHIAVE_RINNOVO);
    return risposta ? await risposta.json() : null;
  } catch {
    return null;
  }
};

const cancellaRinnovo = async () => {
  try {
    const cache = await caches.open(CACHE_RINNOVO);
    await cache.delete(CHIAVE_RINNOVO);
  } catch { /* niente da cancellare */ }
};

// Il browser ha cambiato l'iscrizione mentre l'app era chiusa (public/sw.js l'ha lasciata scritta): qui c'è il
// login, quindi si finisce il lavoro. Senza la nuova iscrizione (il service worker non ce l'ha fatta) si rifà
// tutto da capo, tenendo il vecchio indirizzo per non perdere nome e data del dispositivo.
export const inviaRinnovoInSospeso = async (token = leggiToken()) => {
  const rinnovo = await leggiRinnovo();
  if (!rinnovo || !token) return false;
  await cancellaRinnovo();
  if (rinnovo.subscription?.endpoint) {
    const dispositivo = await salvaSulServer(token, {
      subscription: rinnovo.subscription,
      endpoint_precedente: rinnovo.endpoint_precedente || null,
      // il rinnovo del service worker rifà l'iscrizione con la chiave che il server dava in quel momento:
      // è quella che abbiamo segnato qui (backend/push_service.py la confronta prima di ogni invio)
      chiave_pubblica: leggiChiaveUsata(),
    });
    if (dispositivo?.id) segnaDispositivo({ id: dispositivo.id, nome: dispositivo.nome_dispositivo });
    return Boolean(dispositivo);
  }
  return Boolean(await subscribeToPush());
};

let inAscolto = false;
const ascoltaIlServiceWorker = () => {
  if (inAscolto || !('serviceWorker' in navigator)) return;
  inAscolto = true;
  navigator.serviceWorker.addEventListener('message', (evento) => {
    if (evento.data?.tipo === 'mk-push-rinnovo') inviaRinnovoInSospeso().catch(() => {});
  });
};

// Dopo il login: si riprende quello che era rimasto in sospeso e, se il permesso c'è già, si rinfresca
// l'iscrizione di questo dispositivo (silenziosamente, senza chiedere niente).
export const allineaNotifiche = async () => {
  if (!notifichePossibili()) return;
  try {
    await inviaRinnovoInSospeso();
    if (Notification.permission === 'granted') await subscribeToPush();
  } catch { /* non è mai un problema per l'app */ }
};

// Uscita: al massimo 3 secondi per togliere l'iscrizione alle notifiche, poi si va avanti comunque (chi chiama chiude la
// sessione sul server subito dopo)
export const MS_MASSIMO_DISISCRIZIONE = 3000;

const disiscrivi = async (token) => {
  // se un'iscrizione è partita un attimo fa (login e "Esci" ravvicinati) si aspetta che finisca, altrimenti
  // toglieremmo quella vecchia e il server si terrebbe quella nuova
  if (iscrizioneInCorso) await iscrizioneInCorso.catch(() => null);
  // getRegistration risponde subito, anche senza service worker (undefined); serviceWorker.ready invece aspetta
  // per sempre che se ne registri uno: senza (notifiche mai attivate, sito aperto in una finestra privata…) "Esci"
  // non arrivava mai a chiudere la sessione sul server
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = registration?.pushManager ? await registration.pushManager.getSubscription() : null;
  dimenticaDispositivo();
  if (!subscription) return;
  if (token) {
    try {
      await fetch(`${API}/push/unsubscribe`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
    } catch (error) {
      console.error('[Push] Errore unsubscribe sul server:', error); // l'iscrizione si toglie lo stesso dal dispositivo
    }
  }
  await subscription.unsubscribe();
};

// token: quello della sessione che esce (letto subito, prima che la sessione si cancelli). Non si ferma mai: risolve
// appena finito, dopo un errore o dopo `massimo` ms.
export const unsubscribeFromPush = async (token = leggiToken(), massimo = MS_MASSIMO_DISISCRIZIONE) => {
  if (!('serviceWorker' in navigator) || typeof navigator.serviceWorker?.getRegistration !== 'function') return;
  let timer = null;
  try {
    await Promise.race([
      disiscrivi(token),
      new Promise((fatto) => { timer = setTimeout(fatto, massimo); }),
    ]);
  } catch (error) {
    console.error('[Push] Errore unsubscribe:', error);
  } finally {
    clearTimeout(timer);
  }
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
